<template>
  <div class="patient-vitals">
    <form v-if="state === 'create'" @submit.prevent="submitVitals">
      <div class="grid grid-cols-3 gap-x-10 gap-y-2 overflow-y-auto">
        <!-- Temperature -->
        <div class="form-group">
          <label for="temperature" class="block text-sm font-medium text-gray-700">
            Temperature
          </label>
          <input
            type="number"
            id="temperature"
            v-model="vitals.temperature"
            :class="{
              'border-red-500 bg-red-300':
                (vitals.temperature < 35.5 && vitals.temperature) ||
                vitals.temperature > 37.2,
            }"
            class="mt-1 block w-full rounded-md shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 border-gray-300 bg-gray-50"
            placeholder="°C"
          />
        </div>

        <!-- Pulse -->
        <div class="form-group">
          <label for="pulse" class="block text-sm font-medium text-gray-700">Pulse</label>
          <input
            type="number"
            id="pulse"
            v-model="vitals.pulse"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
            :class="{ 'bg-red-300': isVitalHigh('heartRate', vitals.pulse) }"
            placeholder="rate/min"
          />
        </div>

        <!-- Respiratory Rate -->
        <div class="form-group">
          <label for="respiratoryRate" class="block text-sm font-medium text-gray-700">
            Respiratory Rate
          </label>
          <input
            type="number"
            id="respiratoryRate"
            v-model="vitals.respiratory_rate"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
            :class="{ 'bg-red-300': isVitalHigh('respiration', vitals.respiratory_rate) }"
            placeholder="Respiratory"
          />
        </div>

        <!-- Blood Pressure -->
        <div class="form-group">
          <label for="bloodPressure" class="block text-sm font-medium text-gray-700">
            Blood Pressure
          </label>
          <input
            type="text"
            id="bloodPressure"
            v-model="vitals.blood_pressure"
            @change="classifyBloodPressure(vitals.blood_pressure)"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
            :class="{ 'bg-red-300': flag.bp.flagged }"
            placeholder="mm/Hg e.g 120/90"
          />
          <span v-if="flag.bp.flagged" class="text-red-500 text-sm">{{ flag.bp.label }}</span>
        </div>

        <!-- Oxygen Saturation -->
        <div class="form-group">
          <label for="oxygenSaturation" class="block text-sm font-medium text-gray-700">
            Oxygen Saturation
          </label>
          <input
            type="number"
            id="oxygenSaturation"
            v-model="vitals.oxygen_saturation"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
            placeholder="Oxygen"
          />
        </div>

        <!-- Weight -->
        <div class="form-group">
          <label for="weight" class="block text-sm font-medium text-gray-700">Weight</label>
          <input
            type="number"
            id="weight"
            @change="calculate"
            v-model.number="vitals.weight"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
            placeholder="kg"
          />
        </div>

        <!-- Height -->
        <div class="form-group">
          <label for="height" class="block text-sm font-medium text-gray-700">Height</label>
          <input
            type="number"
            id="height"
            @change="calculate"
            v-model.number="vitals.height"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
            placeholder="cm"
          />
        </div>

        <!-- BMI -->
        <div class="form-group">
          <label for="bmi" class="block text-sm font-medium text-gray-700">BMI</label>
          <input
            type="number"
            id="bmi"
            v-model="vitals.bmi"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
            :class="{ 'bg-red-300': isVitalHigh('bmi', vitals.bmi) }"
            placeholder="kg/m²"
            readonly
          />
        </div>

        <!-- BSA -->
        <div class="form-group">
          <label for="bsa" class="block text-sm font-medium text-gray-700">BSA</label>
          <input
            type="number"
            id="bsa"
            v-model="vitals.bsa"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
            :class="{ 'bg-red-300': isVitalHigh('bsa', vitals.bsa) }"
            placeholder="m²"
            readonly
          />
        </div>

        <!-- Head Circumference -->
        <div v-if="showHeadCircumference" class="form-group">
          <label for="headCircumference" class="block text-sm font-medium text-gray-700">
            Head Circumference
          </label>
          <input
            type="number"
            id="headCircumference"
            v-model="vitals.head_circumference"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
            placeholder="in cm"
          />
        </div>
      </div>
    </form>

    <div v-if="state === 'view'">
      <div class="grid grid-cols-2 px-4 gap-4 mb-6">
        <div><p class="font-semibold">Temperature::</p><p>35C</p></div>
        <div><p class="font-semibold">BMI:</p><p>kg/m2</p></div>
        <div><p class="font-semibold">Blood Pressure:</p><p>mm/Hg</p></div>
        <div><p class="font-semibold">Respiratory Rate:</p><p>Respiratory</p></div>
        <div><p class="font-semibold">Height::</p><p>175cm</p></div>
        <div><p class="font-semibold">Weight::</p><p>kg</p></div>
        <div><p class="font-semibold">Pulse:</p><p>rate/min</p></div>
        <div><p class="font-semibold">BSA:</p><p>BSA</p></div>
        <div><p class="font-semibold">Oxygen Saturation::</p><p>oxygen</p></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ConsultationPatientVitalsTab",
  props: {
    state: { type: String, required: true },
    visitId: { type: String, required: true },
    showHeadCircumference: { type: Boolean, required: true },
  },
  data() {
    return {
      vitals: {
        temperature: null,
        pulse: null,
        respiratory_rate: null,
        blood_pressure: "",
        oxygen_saturation: null,
        weight: null,
        height: null,
        bmi: null,
        bsa: null,
        head_circumference: null,
        systolic: null,
        diastolic: null,
      },
      flag: {
        bp: { flagged: false, label: "" },
      },
    };
  },
  methods: {
    submitVitals() {
      this.$emit("vitals-submitted", this.vitals);
    },
    calculate() {
      this.bmi();
      this.bsa();
    },
    bmi() {
      const { weight, height } = this.vitals;
      if (weight && height) {
        const heightInMeters = height / 100;
        this.vitals.bmi = (weight / heightInMeters ** 2).toFixed(2);
      }
      return "";
    },
    bsa() {
      const { weight, height } = this.vitals;
      if (weight && height) {
        this.vitals.bsa = Math.sqrt((height * weight) / 3600).toFixed(2);
      }
      return "";
    },
    isVitalHigh(type, value) {
      const thresholds = {
        temperature: 38,
        bloodPressure: 140,
        heartRate: 100,
        respiration: 20,
        bmi: 25,
        bsa: 2.2,
      };
      return value > (thresholds[type] || Infinity);
    },
    classifyBloodPressure(bpString) {
      const [systolic, diastolic] = bpString.split("/").map(Number);
      if (isNaN(systolic) || isNaN(diastolic)) return "Invalid input";
      this.vitals.systolic = systolic;
      this.vitals.diastolic = diastolic;
      if (systolic >= 140 || diastolic >= 90) {
        this.flag.bp.flagged = true;
        this.flag.bp.label = "High Blood Pressure(Hypertension)";
      } else if (systolic < 90 || diastolic < 60) {
        this.flag.bp.flagged = true;
        this.flag.bp.label = "Low Blood Pressure (Hypotension)";
      } else {
        this.flag.bp.flagged = false;
        this.flag.bp.label = "";
        return "Normal Blood Pressure";
      }
    },
  },
};
</script>

<style scoped>
.form-group { margin-bottom: 1rem; }
</style>